import { addToCart, changeProductFromCart, removeCartItem, setOrderParams } from "features/common/layouts/cartSlice";
import { getProduct, selectProduct } from "features/common/products/productsSlice";
import { formatIDR, useQuery } from "helpers/utils";
import MainLayouts from "layouts/MainLayouts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Placeholder } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { notifySuccess, notifyError } from "components/ToastMessage";
import CardWrapper from "components/CardWrapper";
import "./style.css";

const STORAGE_MIN_GB = 10;
const STORAGE_MAX_GB = 1280;

const ObjectStorage = () => {
  const query = useQuery();
  const dispatch = useDispatch();
  const pid = parseInt(query.pid, 10) || 1198;
  const [storageGb, setStorageGb] = useState(STORAGE_MIN_GB);
  const [inputValue, setInputValue] = useState(String(STORAGE_MIN_GB));
  const didInitFromCartRef = useRef(false);
  const didClearPersistedCartRef = useRef(false);
  const isMountedRef = useRef(true);
  const didApplyDiskParamRef = useRef({ pid: null, disk: null });
  const configOptionReqIdRef = useRef(0);
  const [fallbackOptionId, setFallbackOptionId] = useState(null);
  const [fallbackPerGbMonthly, setFallbackPerGbMonthly] = useState(0);
  const [loadingConfigOption, setLoadingConfigOption] = useState(false);
  const [configOptionError, setConfigOptionError] = useState("");
  const [configOptionLookupDone, setConfigOptionLookupDone] = useState(false);

  const currentProduct = useSelector(selectProduct);
  const { current, status } = currentProduct;
  const cartItems = useSelector((state) => state.cart.productsList);
  const { rehydrated } = useSelector((state) => state._persist ?? { rehydrated: true });

  const existingCartItem = useMemo(() => {
    if (!Array.isArray(cartItems)) return null;
    return cartItems.find((item) => item?.type === "objectStorage" && Number(item?.pid) === Number(pid)) || null;
  }, [cartItems, pid]);

  useEffect(() => {
    dispatch(setOrderParams({ pid, billingcycle: "monthly" }));
  }, [pid, dispatch]);

  const clampGb = useCallback((val) => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n)) return null;
    return Math.min(STORAGE_MAX_GB, Math.max(STORAGE_MIN_GB, n));
  }, []);

  const diskGbFromQuery = useMemo(() => clampGb(query.disk), [query.disk, clampGb]);

  // Support param URL `?disk=xx` agar slider langsung ke angka tsb
  useEffect(() => {
    if (diskGbFromQuery === null) return;
    const prev = didApplyDiskParamRef.current || { pid: null, disk: null };
    if (Number(prev.pid) === Number(pid) && Number(prev.disk) === Number(diskGbFromQuery)) return;
    didApplyDiskParamRef.current = { pid, disk: diskGbFromQuery };
    setStorageGb(diskGbFromQuery);
    setInputValue(String(diskGbFromQuery));
    console.log("[ObjectStorage] applied disk param", { pid, disk: diskGbFromQuery });
  }, [diskGbFromQuery, pid]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    didInitFromCartRef.current = false;
    setFallbackOptionId(null);
    setFallbackPerGbMonthly(0);
    setLoadingConfigOption(false);
    setConfigOptionError("");
    setConfigOptionLookupDone(false);
  }, [pid]);

  useEffect(() => {
    dispatch(getProduct(pid));
  }, [pid, dispatch]);

  useEffect(() => {
    console.log("[ObjectStorage] mounted", { pid });
  }, [pid]);

  // Pastikan halaman mulai dari cart kosong untuk Object Storage (sesuai request)
  useEffect(() => {
    if (!rehydrated) return;
    if (didClearPersistedCartRef.current) return;
    didClearPersistedCartRef.current = true;
    if (!existingCartItem) return;
    const prevOptionId = existingCartItem?.configoptions?.[0]?.id ?? null;
    if (prevOptionId) setFallbackOptionId(prevOptionId);
    dispatch(removeCartItem(pid));
    didInitFromCartRef.current = true; // jangan re-init slider dari cart lama
    const nextGb = diskGbFromQuery ?? STORAGE_MIN_GB;
    setStorageGb(nextGb);
    setInputValue(String(nextGb));
  }, [rehydrated, existingCartItem, pid, dispatch, diskGbFromQuery]);

  const toNumber = useCallback((val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === "number") return Number.isFinite(val) ? val : 0;
    const normalized = String(val).replace(/[^\d.-]/g, "");
    const num = parseFloat(normalized);
    return Number.isFinite(num) ? num : 0;
  }, []);

  const storageOption = useMemo(() => {
    const options = current?.configoptions?.configoption;
    if (!Array.isArray(options)) return null;
    return (
      options.find(
        (o) =>
          (o.optionname || o.name || "").toLowerCase().includes("storage") &&
          ((o.type || "").toLowerCase() === "quantity" || (o.type || "").toLowerCase() === "qty")
      ) || options.find((o) => (o.type || "").toLowerCase() === "quantity" || (o.type || "").toLowerCase() === "qty")
    );
  }, [current?.configoptions?.configoption]);

  // Fallback: ambil configoptionId "Storage (GB)" dari backend (DB WHMCS)
  useEffect(() => {
    if (status !== "idle") return;
    if (storageOption?.id) return;
    if (fallbackOptionId) return;
    if (configOptionLookupDone) return;

    const reqId = ++configOptionReqIdRef.current;
    const run = async () => {
      try {
        setLoadingConfigOption(true);
        setConfigOptionError("");

        const baseApi = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");
        const basePath = (process.env.REACT_APP_BASE_PATH || "").replace(/\/+$/, "");
        const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");

        const candidates = [
          `${baseApi}/order/apis/getConfigOptionByPid.php?pid=${pid}`,
          basePath ? `${baseApi}${basePath}/order/apis/getConfigOptionByPid.php?pid=${pid}` : null,
          publicUrl ? `${publicUrl}/order/apis/getConfigOptionByPid.php?pid=${pid}` : null,
        ].filter(Boolean);

        let json = null;
        let lastErr = null;
        for (const url of candidates) {
          try {
            const res = await fetch(url);
            const txt = await res.text();
            const parsed = txt ? JSON.parse(txt) : null;
            if (parsed && (parsed.code === 200 || parsed.code === 404 || parsed.code === 400)) {
              json = parsed;
              break;
            }
          } catch (e) {
            lastErr = e;
          }
        }

        if (!json) {
          throw lastErr || new Error("No response from getConfigOptionByPid");
        }

        // Hanya update state jika ini request terakhir & component masih mounted
        if (!isMountedRef.current || configOptionReqIdRef.current !== reqId) return;

        if (json?.code === 200 && json?.data?.configoption?.id) {
          setFallbackOptionId(json.data.configoption.id);
          const perGb = json?.data?.configoption?.pricing?.IDR?.monthly;
          setFallbackPerGbMonthly(toNumber(perGb));
          console.log("[ObjectStorage] resolved configoption", {
            pid,
            id: json.data.configoption.id,
            perGbMonthly: toNumber(perGb),
          });
        } else {
          console.warn("[ObjectStorage] failed to resolve configoption", { pid, json });
          if (json?.message === "No configurable options linked to this product") {
            setConfigOptionError(
              "Produk ini belum terhubung ke WHMCS Configurable Options (Storage (GB)). Invoice akan selalu memakai harga dasar (Rp15.000/bulan) sampai Configurable Options di-link."
            );
          } else {
            setConfigOptionError(json?.message || "Configurable Option Storage (GB) tidak ditemukan");
          }
        }

        setConfigOptionLookupDone(true);
      } catch (e) {
        if (!isMountedRef.current || configOptionReqIdRef.current !== reqId) return;
        console.warn("[ObjectStorage] resolve configoption error", e);
        setConfigOptionError("Gagal memuat Configurable Option Storage (GB)");
        setConfigOptionLookupDone(true);
      } finally {
        if (!isMountedRef.current || configOptionReqIdRef.current !== reqId) return;
        setLoadingConfigOption(false);
      }
    };

    run();
  }, [status, pid, storageOption?.id, fallbackOptionId, configOptionLookupDone, toNumber]);

  const baseMonthlyPrice = useMemo(() => {
    const idr = current?.pricing?.IDR;
    if (!idr) return 0;
    return toNumber(idr.monthly ?? idr.Monthly ?? 0);
  }, [current?.pricing?.IDR, toNumber]);

  const pricePerGbMonthly = useMemo(() => {
    // WHMCS LocalAPI shape can differ per configoption type.
    // Try the most common places first, then fall back.
    const optionNode = Array.isArray(storageOption?.options?.option)
      ? storageOption.options.option[0]
      : storageOption?.options?.option;

    const pricingIDR =
      optionNode?.pricing?.IDR ||
      optionNode?.pricing?.idr ||
      storageOption?.pricing?.IDR ||
      storageOption?.pricing?.idr ||
      null;

    const monthly = pricingIDR?.monthly ?? pricingIDR?.Monthly ?? pricingIDR?.[0]?.monthly ?? pricingIDR?.[0]?.Monthly;
    return toNumber(monthly);
  }, [storageOption, toNumber]);

  const derivedPricePerGbMonthly = useMemo(() => {
    const gb = Number(existingCartItem?.configoptions?.[0]?.value || 0);
    const price = Number(existingCartItem?.price || 0);
    if (!gb || !price) return 0;
    const perGb = price / gb;
    return Number.isFinite(perGb) && perGb > 0 ? perGb : 0;
  }, [existingCartItem]);

  const effectivePricePerGbMonthly = useMemo(() => {
    if (fallbackPerGbMonthly > 0) return fallbackPerGbMonthly;
    if (pricePerGbMonthly > 0) return pricePerGbMonthly;
    if (derivedPricePerGbMonthly > 0) return derivedPricePerGbMonthly;
    // Fallback: jika API tidak mengembalikan pricing configoptions, turunkan dari base price minimum.
    if (baseMonthlyPrice > 0) return baseMonthlyPrice / STORAGE_MIN_GB;
    return 0;
  }, [fallbackPerGbMonthly, pricePerGbMonthly, derivedPricePerGbMonthly, baseMonthlyPrice]);

  const priceMonthly = useMemo(() => {
    const gb = Math.min(STORAGE_MAX_GB, Math.max(STORAGE_MIN_GB, Number(storageGb) || STORAGE_MIN_GB));
    return (effectivePricePerGbMonthly || 0) * gb;
  }, [storageGb, effectivePricePerGbMonthly]);

  const clampedGb = useMemo(
    () => Math.min(STORAGE_MAX_GB, Math.max(STORAGE_MIN_GB, Number(storageGb) || STORAGE_MIN_GB)),
    [storageGb]
  );

  const handleSliderChange = useCallback(
    (e) => {
      const val = parseInt(e.target.value, 10);
      const gb = Math.min(STORAGE_MAX_GB, Math.max(STORAGE_MIN_GB, isNaN(val) ? STORAGE_MIN_GB : val));
      setStorageGb(gb);
      setInputValue(String(gb));
      console.log("[ObjectStorage] slider change", { gb });
    },
    []
  );

  const handleInputChange = useCallback((e) => {
    const raw = e.target.value.replace(/\D/g, "");
    setInputValue(raw || "");
    if (raw === "") return;
    const num = parseInt(raw, 10);
    if (!isNaN(num)) {
      const gb = Math.min(STORAGE_MAX_GB, Math.max(STORAGE_MIN_GB, num));
      setStorageGb(gb);
      setInputValue(String(gb));
    }
  }, []);

  const handleInputBlur = useCallback(() => {
    const num = parseInt(inputValue, 10);
    const gb = isNaN(num) ? STORAGE_MIN_GB : Math.min(STORAGE_MAX_GB, Math.max(STORAGE_MIN_GB, num));
    setStorageGb(gb);
    setInputValue(String(gb));
  }, [inputValue]);

  useEffect(() => {
    if (status !== "idle") return;
    console.log("[ObjectStorage] pricing", {
      pid,
      gb: clampedGb,
      pricePerGbMonthly: effectivePricePerGbMonthly,
      priceMonthly,
      baseMonthlyPrice,
      storageOptionId: storageOption?.id ?? null,
      fallbackOptionId,
    });
  }, [
    status,
    pid,
    clampedGb,
    effectivePricePerGbMonthly,
    priceMonthly,
    baseMonthlyPrice,
    storageOption?.id,
    fallbackOptionId,
  ]);

  // Jika item Object Storage sudah ada di keranjang, update harga & GB saat slider digeser
  // (tanpa auto-add item baru)
  useEffect(() => {
    if (status !== "idle") return;
    if (!existingCartItem) return;

    const prevGb = Number(existingCartItem?.configoptions?.[0]?.value || 0);
    const prevPrice = Number(existingCartItem?.price || 0);
    const nextGb = Number(clampedGb || 0);
    const nextPrice = Number(priceMonthly || 0);

    if (prevGb === nextGb && prevPrice === nextPrice) return;

    const optionId = existingCartItem?.configoptions?.[0]?.id ?? null;
    dispatch(
      changeProductFromCart({
        pid,
        name: existingCartItem?.name || current?.name || "Object Storage | Qwords",
        price: nextPrice,
        billingcycle: existingCartItem?.billingcycle || "monthly",
        type: "objectStorage",
        configoptions: [{ ...(optionId ? { id: optionId } : {}), value: nextGb }],
      })
    );
  }, [status, existingCartItem, clampedGb, priceMonthly, pid, dispatch, current?.name]);

  const addToCartHandler = () => {
    if (status !== "idle" || !current) return;
    const optionId = storageOption?.id ?? fallbackOptionId ?? existingCartItem?.configoptions?.[0]?.id ?? null;
    const productDetails = {
      pid,
      name: current.name || "Object Storage | Qwords",
      price: priceMonthly, // total harga ditampilkan di item.price
      billingcycle: "monthly",
      type: "objectStorage",
      configoptions: [
        {
          id: optionId,
          value: clampedGb,
        },
      ],
    };
    console.log("[ObjectStorage] addToCart click", {
      pid,
      optionId,
      gb: clampedGb,
      priceMonthly,
      hasExistingCartItem: !!existingCartItem,
    });
    if (!productDetails.configoptions?.[0]?.id) {
      notifyError("Gagal mengambil Configurable Option Storage (GB). Coba refresh halaman, atau cek setting WHMCS Configurable Options.");
      return;
    }
    if (existingCartItem) {
      dispatch(changeProductFromCart(productDetails));
      notifySuccess("Object Storage berhasil diperbarui di keranjang.");
    } else {
      dispatch(addToCart(productDetails));
      notifySuccess("Object Storage berhasil ditambahkan ke keranjang.");
    }
  };

  const displayName = (current?.name || "Object Storage | Qwords").replace(/nevaobjects?/i, "Qwords").trim() || "Object Storage | Qwords";

  return (
    <MainLayouts summary={false}>
      <CardWrapper title="">
        <div className="object-storage-page">
          <h2 className="object-storage-title">
            {displayName}
          </h2>
          <p className="object-storage-desc">
            Solusi penyimpanan data dalam jumlah besar, kompatibel dengan protokol S3. Qwords menawarkan layanan
            penyimpanan dengan performa maksimal, skalabilitas, keamanan, dan harga terjangkau.
          </p>

          <div className="object-storage-config row align-items-start">
            <div className="col-12 col-lg-7">
              <label className="object-storage-label">Storage:</label>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <input
                  type="text"
                  className="form-control object-storage-input"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  min={STORAGE_MIN_GB}
                  max={STORAGE_MAX_GB}
                  aria-label="Jumlah storage dalam GB"
                />
                <span className="object-storage-unit">GB</span>
              </div>
              <div className="object-storage-slider-wrap mt-2">
                <input
                  type="range"
                  className="object-storage-slider"
                  min={STORAGE_MIN_GB}
                  max={STORAGE_MAX_GB}
                  value={clampedGb}
                  onChange={handleSliderChange}
                  aria-label="Pilih kapasitas storage"
                />
                <div className="d-flex justify-content-between mt-1 small text-muted">
                  <span>{STORAGE_MIN_GB} GB</span>
                  <span>{STORAGE_MAX_GB.toLocaleString("id-ID")} GB (1,2 TB)</span>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-5 mt-3 mt-lg-0 text-lg-end">
              {status === "idle" ? (
                <div className="object-storage-price">
                  <span className="text-muted">Harga: </span>
                  <strong className="text-dark">{formatIDR(priceMonthly)}</strong>
                  <span className="text-muted"> /Bulan</span>
                </div>
              ) : (
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={6} />
                </Placeholder>
              )}
            </div>
          </div>

          <div className="object-storage-actions mt-4">
            <button
              type="button"
              className="btn btn-cta object-storage-btn-primary rounded-5 px-4 py-2 fw-bold"
              onClick={addToCartHandler}
              disabled={status !== "idle" || loadingConfigOption || !(storageOption?.id || fallbackOptionId)}
            >
              {loadingConfigOption ? "Memuat Opsi..." : "Tambahkan Ke Keranjang"}
            </button>
            {status === "idle" && !loadingConfigOption && configOptionError && (
              <div className="small text-danger mt-2">
                {configOptionError}
              </div>
            )}
          </div>

          <div id="detail-harga" className="object-storage-detail mt-5 pt-3">
            <h6 className="fw-bold mb-2">Detail Harga</h6>
            <p className="small text-muted mb-0">
              Harga per GB: {formatIDR(effectivePricePerGbMonthly)}/bulan. Total untuk {clampedGb.toLocaleString("id-ID")} GB
              = {formatIDR(priceMonthly)}/bulan. Minimum {STORAGE_MIN_GB} GB, maksimum {STORAGE_MAX_GB.toLocaleString("id-ID")} GB (1,2 TB).
            </p>
          </div>
        </div>
      </CardWrapper>
    </MainLayouts>
  );
};

export default ObjectStorage;
